/**
 * Auth + cloud save client.
 *
 * Accounts live on Cloudflare Pages Functions (/api/register, /api/login,
 * /api/save) backed by KV. The session token is kept in localStorage; the
 * cloud save is the same JSON the local save system writes, so whichever is
 * NEWER (lastSavedAt) wins when a player logs in on any device.
 *
 * The login screen is a full-screen overlay styled like the site — shown when
 * the player hits "Jogar Agora" without a session. No e-mail verification for
 * now (test phase); passwords are hashed server-side.
 *
 * @module ui/auth
 */

import { bus } from '../core/events.js';

const AUTH_KEY = 'pocketdungeon.auth';
const SAVE_KEY = 'pocketdungeon.save';

// ---------------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------------

export function getSession() {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return null;
}

function setSession(session) {
  try {
    if (session) localStorage.setItem(AUTH_KEY, JSON.stringify(session));
    else localStorage.removeItem(AUTH_KEY);
  } catch { /* ignore */ }
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

async function api(path, { method = 'GET', body, token } = {}) {
  const headers = {};
  if (body != null) headers['content-type'] = 'application/json';
  if (token) headers['authorization'] = `Bearer ${token}`;
  const res = await fetch(path, {
    method,
    headers,
    body: body != null ? (typeof body === 'string' ? body : JSON.stringify(body)) : undefined,
  });
  let data = null;
  try { data = await res.json(); } catch { /* non-JSON error page */ }
  if (!res.ok) {
    const msg = data?.error ?? `Erro ${res.status} — tente de novo.`;
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  return data;
}

/**
 * Pull the cloud save; if it's newer than the local one, adopt it (the caller
 * must reload so the game boots from the adopted save). If the local one is
 * newer (or the cloud is empty), push local up instead.
 * @returns {Promise<'adopted'|'pushed'|'none'>}
 */
export async function syncOnLogin(token) {
  const { save: cloud } = await api('/api/save', { token });
  let local = null;
  try { local = JSON.parse(localStorage.getItem(SAVE_KEY) ?? 'null'); } catch { /* ignore */ }

  if (cloud && (!local || (cloud.lastSavedAt ?? 0) > (local.lastSavedAt ?? 0))) {
    localStorage.setItem(SAVE_KEY, JSON.stringify(cloud));
    return 'adopted';
  }
  if (local) {
    await api('/api/save', { method: 'PUT', token, body: JSON.stringify(local) });
    return 'pushed';
  }
  return 'none';
}

/** Timestamp of the last successful cloud push (0 = none this session). */
let _lastPushAt = 0;
export function lastPushAt() { return _lastPushAt; }

/** Push the current local save to the cloud. Resolves true on success. */
export async function pushSave() {
  const session = getSession();
  if (!session?.token) return false;
  const raw = localStorage.getItem(SAVE_KEY);
  if (!raw) return false;
  try {
    await api('/api/save', { method: 'PUT', token: session.token, body: raw });
    _lastPushAt = Date.now();
    bus.emit('cloud:pushed', { at: _lastPushAt });
    return true;
  } catch (err) {
    // 401 = session expired: drop it so the next visit asks for login again.
    if (err.status === 401) setSession(null);
    return false;
  }
}

/** End the session and go back to the landing page (fresh login required). */
export function logout() {
  setSession(null);
  window.location.reload();
}

/** Start periodic cloud pushes (every 5 min + on tab hide). Idempotent.
 *  5 min (not 60s): the server only persists one write / 10 min / account
 *  anyway (free-tier KV allows 1000 writes/day for EVERYONE — a single
 *  player syncing every minute burned the whole quota and 1101'd the API). */
let _syncStarted = false;
export function startCloudSync() {
  if (_syncStarted || !getSession()) return;
  _syncStarted = true;
  setInterval(pushSave, 300_000);
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') pushSave();
  });
}

// ---------------------------------------------------------------------------
// Login screen
// ---------------------------------------------------------------------------

/**
 * Show the login/register overlay. Resolves 'ok' once the player is
 * authenticated (session stored) or 'cancelled' if they close it (✕ / Esc).
 * If the cloud save was adopted, reloads the page so the game boots from it —
 * in that case the promise never resolves.
 * @returns {Promise<'ok'|'cancelled'>}
 */
export function showLoginScreen() {
  return new Promise((resolve) => {
    let mode = 'login'; // 'login' | 'register'

    const overlay = document.createElement('div');
    overlay.className = 'pd-login-overlay';
    overlay.innerHTML = `
      <div class="pd-login-card">
        <button type="button" class="pd-login-close" aria-label="Fechar">✕</button>
        <img class="pd-login-logo" src="assets/brand/logo.png" alt="PocketDungeon" />
        <h2 class="pd-login-title">PocketDungeon</h2>
        <p class="pd-login-sub"></p>
        <form class="pd-login-form">
          <input type="text" name="nick" placeholder="seu nick (aparece no ranking)" autocomplete="nickname" minlength="3" maxlength="16" hidden />
          <input type="email" name="email" placeholder="seu@email.com" autocomplete="email" required />
          <input type="password" name="password" placeholder="senha" autocomplete="current-password" minlength="6" required />
          <div class="pd-login-error" hidden></div>
          <button type="submit" class="pd-login-submit">Entrar</button>
        </form>
        <button class="pd-login-switch"></button>
      </div>`;

    const close = () => {
      document.removeEventListener('keydown', onKey);
      overlay.remove();
      resolve('cancelled');
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    overlay.querySelector('.pd-login-close').addEventListener('click', close);

    const sub = overlay.querySelector('.pd-login-sub');
    const form = overlay.querySelector('.pd-login-form');
    const errorEl = overlay.querySelector('.pd-login-error');
    const submitBtn = overlay.querySelector('.pd-login-submit');
    const switchBtn = overlay.querySelector('.pd-login-switch');
    const passwordEl = form.querySelector('[name="password"]');
    const nickEl = form.querySelector('[name="nick"]');

    const applyMode = () => {
      sub.textContent = mode === 'login'
        ? 'Entre para continuar sua aventura — seu progresso te acompanha em qualquer dispositivo.'
        : 'Crie sua conta para começar — seu nick entra no ranking e o progresso fica salvo na nuvem.';
      submitBtn.textContent = mode === 'login' ? 'Entrar' : 'Criar conta';
      switchBtn.textContent = mode === 'login' ? 'Não tem conta? Criar uma' : 'Já tem conta? Entrar';
      passwordEl.autocomplete = mode === 'login' ? 'current-password' : 'new-password';
      nickEl.hidden = mode === 'login';
      nickEl.required = mode === 'register';
      errorEl.hidden = true;
    };
    applyMode();

    switchBtn.addEventListener('click', () => { mode = mode === 'login' ? 'register' : 'login'; applyMode(); });

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      errorEl.hidden = true;
      submitBtn.disabled = true;
      submitBtn.textContent = '...';
      try {
        const email = form.email.value;
        const password = form.password.value;
        const body = { email, password };
        if (mode === 'register') body.nick = form.nick.value;
        const { token, nick } = await api(`/api/${mode}`, { method: 'POST', body });
        setSession({ token, email: email.trim().toLowerCase(), nick: nick ?? null });

        const result = await syncOnLogin(token).catch(() => 'none');
        if (result === 'adopted') {
          // Boot from the (newer) cloud save.
          window.location.reload();
          return;
        }
        startCloudSync();
        // Enter the board right away instead of waiting for the 60s sync tick.
        pushSave();
        bus.emit('auth:login', getSession());
        document.removeEventListener('keydown', onKey);
        overlay.remove();
        resolve('ok');
      } catch (err) {
        errorEl.textContent = err.message;
        errorEl.hidden = false;
        applyMode(); // restore button label
        submitBtn.disabled = false;
      }
    });

    document.body.appendChild(overlay);
    overlay.querySelector('[name="email"]').focus();
  });
}
